import { HttpClient } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { API_URL } from '@constants';
import {
  AnyResource,
  GroupSubType,
  Permission,
  PermissionItem,
  PermissionLevel,
  PermissionLevelItem,
  PermissionsSelection,
  ResourceType,
  RoomSubType,
  SelectionItem,
  UserSubType,
} from '@models';
import {
  getParentTypeByResource,
  getPathByType,
  logError,
} from '@shared/utils';
import { onSessionEnd } from '@store';
import { ModalService } from './modal.service';

interface PermissionSelectionItem extends SelectionItem {
  level: number;
  inUse?: boolean;
  local_inheritance?: boolean;
}

type ResourceWithPermissions = Omit<AnyResource, 'permissions'> & {
  permissions?: {
    items: Permission[];
  };
  roomId?: number;
  roomPermission?: number;
  parentType?: number;
  invitees?: SelectionItem[];
};

@Service()
export class PermissionService {
  http = inject(HttpClient);
  modalService = inject(ModalService);
  translate = inject(TranslateService);

  constructor() {
    onSessionEnd(this.reset);
  }

  getPermissionsPreview = (permissionsItems: {
    items: PermissionSelectionItem[];
  }) =>
    firstValueFrom(
      this.http.post<Permission[]>(`${API_URL}/permissions/overview`, {
        ...permissionsItems,
        items: permissionsItems.items.map(
          ({ local_inheritance, ...rest }) => rest
        ),
      })
    );

  isMandatory = (permissionItemType: number, subtype: number) =>
    permissionItemType === ResourceType.COMMUNITY &&
    subtype === UserSubType.ROOT;

  isDeletable = (
    listLevels: { level: number; items: PermissionSelectionItem[] }[],
    permissionItem: PermissionSelectionItem
  ) =>
    !(
      permissionItem.level === PermissionLevel.MODIFY_AND_DELETE &&
      listLevels.some(
        ({ level, items }) =>
          level === PermissionLevel.MODIFY_AND_DELETE && items.length === 1
      )
    );

  currentPermission: Permission[] = [];
  currentPid: Nullable<number> = null;
  currentTypeResource: Nullable<number> = null;
  communityMandatory = false;

  getExternalList = (selectionList: SelectionItem[]) => {
    if (!selectionList) {
      return;
    }

    return selectionList.filter(
      (el) =>
        (el.type === ResourceType.USER &&
          (el.subtype === UserSubType.GUEST ||
            el.subtype === UserSubType.ONE_TIME_GUEST)) ||
        (el.type === ResourceType.GROUP && el.subtype === GroupSubType.GUESTS)
    );
  };

  setCurrentPid = (pid: Nullable<number>) => {
    this.currentPid = pid;
  };

  reset = () => {
    this.currentPermission = [];
    this.currentPid = null;
    this.currentTypeResource = null;
    this.communityMandatory = false;
  };

  getCurrentPid = () => this.currentPid;

  setCurrentTypeResource = (typeResource: Nullable<number>) => {
    this.currentTypeResource = typeResource;
  };

  getCurrentTypeResource = () => {
    this.currentPermission = this.currentPermission.map((permission) => ({
      ...permission,
      inUse: true,
    }));

    return this.currentTypeResource;
  };

  setCommunityMandatory = (value: unknown) => {
    this.communityMandatory = value !== null;
  };

  getCommunityMandatory = () => this.communityMandatory;

  setCurrentPermissions = (
    allPermissionItems: (Permission | PermissionItem)[] = []
  ) => {
    this.currentPermission = allPermissionItems.filter((el) => el.inUse);
  };

  getDefaultLevel = (levels: PermissionLevelItem[]) => {
    let level = PermissionLevel.MODIFY_AND_DELETE;

    for (const { value } of levels) {
      if (value === PermissionLevel.ACCESS) {
        return PermissionLevel.ACCESS;
      }

      if (value > 0 && value < level) {
        level = value;
      }
    }

    return level;
  };

  areYouAllowedToDelete = (
    currentPermission: Permission[],
    permissionItem: Permission
  ) => {
    if (permissionItem.level === PermissionLevel.MODIFY_AND_DELETE) {
      const onlyOwner = currentPermission.filter(
        (el) => el.level === PermissionLevel.MODIFY_AND_DELETE
      );

      return onlyOwner.length !== 1;
    }

    return true;
  };

  areYouAllowedToChange = (itemToChange: Permission, newLevel: number) => {
    if (newLevel !== PermissionLevel.MODIFY_AND_DELETE) {
      const onlyOwner = this.currentPermission.filter(
        (el) => el.level === PermissionLevel.MODIFY_AND_DELETE
      );

      return !(
        onlyOwner.length === 1 && onlyOwner[0]?.rid === itemToChange.rid
      );
    }

    return true;
  };

  getCurrentPermissionsPreview = () =>
    firstValueFrom(
      this.http.post<Permission[]>(`${API_URL}/permissions/overview`, {
        items: this.currentPermission.map((p) =>
          p.level === 3.5 ? { ...p, level: 4, inheritance: -1 } : p
        ),
      })
    );

  getDisplayName = (resource: ResourceWithPermissions) => {
    if (resource.type === ResourceType.COMMUNITY) {
      return resource.name;
    } else if (
      resource.type === ResourceType.USER &&
      resource.subtype === UserSubType.ROOT
    ) {
      return this.translate.instant('WEB_USER_LIST');
    } else if (
      resource.type === ResourceType.GROUP &&
      resource.subtype === GroupSubType.ROOT
    ) {
      return this.translate.instant('WEB_GROUP_LIST');
    } else if (
      resource.type === ResourceType.ROOM &&
      resource.subtype === RoomSubType.ROOMS_ROOT
    ) {
      return this.translate.instant('WEB_ROOM_LIST');
    }

    return resource.displayName;
  };

  getRoomId = (resource: ResourceWithPermissions) => {
    return (resource.type === ResourceType.USER ||
      resource.type === ResourceType.GROUP) &&
      resource.roomId &&
      resource.roomId > 0 &&
      resource.roomPermission &&
      resource.roomPermission > 0
      ? resource.roomId
      : null;
  };

  getParentType = (resource: ResourceWithPermissions) => {
    if (resource.parentType === undefined) {
      return getParentTypeByResource(resource as unknown as AnyResource);
    } else {
      return resource.parentType;
    }
  };

  editResourcePermissions = async (resource: ResourceWithPermissions) => {
    const resourceType = getPathByType(resource.type);

    const promise =
      resource?.permissions?.items === undefined
        ? resource.type !== ResourceType.COMMUNITY
          ? firstValueFrom(
              this.http.get<ResourceWithPermissions>(
                `${API_URL}/${resourceType}/${resource.rid}`
              )
            )
          : firstValueFrom(
              this.http.get<ResourceWithPermissions>(
                `${API_URL}/${resourceType}`
              )
            )
        : null;

    try {
      if (promise) {
        resource = await promise;
      }

      const { EditPermissionsModalComponent } =
        await import('@shared/components/permission/edit-permissions-modal.component');

      return this.modalService.open(
        EditPermissionsModalComponent,
        {
          displayName: this.getDisplayName(resource),
          rid: resource.rid,
          currentPermissions: [...(resource.permissions?.items ?? [])],
          pid: resource.pid,
          type: resource.type,
          roomId: this.getRoomId(resource),
          parentType: this.getParentType(resource),
          updateDate: resource.updateDate,
          breadcrumb: resource.breadcrumb,
        },
        {
          modalDialogClass: 'lg-modal',
        }
      );
    } catch (error) {
      logError(error);

      return Promise.reject(error);
    }
  };

  openPermissionsOverview = async (permissions: Permission[]) => {
    const { PermissionsOverviewModalComponent } =
      await import('@shared/components/permission/permissions-overview-modal.component');

    return this.modalService.open(
      PermissionsOverviewModalComponent,
      { permissions },
      {
        modalDialogClass: 'sm-modal',
      }
    );
  };

  getPermissionsSelection = (rid: number) =>
    this.http.get<PermissionsSelection>(
      `${API_URL}/permissions/selection/${rid}`
    );

  getPermissionsOverview = (rid: number) =>
    this.http.get<Permission[]>(`${API_URL}/permissions/overview/${rid}`);
}
